import { isEqual } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getGroupFilterTransformer,
  GroupFieldsMeta,
} from 'components/pages/filter-transformer';
import { FilterGroupType } from 'components/pages/types';
import { useSearch } from 'hooks/use-search';
import { queueMacrotask } from 'utils/macrotask';
import { FilterFunction } from './filter-functions';
import { FieldFilter } from './filters';
import { getGroupedFilterFunction } from './get-filter-function';
import { GroupedFilter } from './grouped-filter-popover';
import { defaultGroupedFilter } from './utils';

interface Filters {
  selectFilter: (next: GroupedFilter) => void;
  replaceFilter: (next: GroupedFilter) => void;
  clearFilters: () => void;
  filter: GroupedFilter;
  filterFunction: FilterFunction | null;
}

export const useGroupFilters = (
  groupFieldsMeta: GroupFieldsMeta,
  defaultGroup: FilterGroupType,
  canSetFilter = false,
): Filters => {
  const transformer = useMemo(
    () => getGroupFilterTransformer(groupFieldsMeta, defaultGroup),
    [groupFieldsMeta, defaultGroup],
  );
  const [search, setSearch] = useSearch(transformer);
  const [filter, setFilter] = useState(search);

  const selectFilter = useCallback(
    (new_filter: GroupedFilter) => {
      if (!canSetFilter) {
        return;
      }

      /* TODO:: This approach of setting two state is not scalable. We need to migrate to single state directly from URL. */
      setFilter(new_filter);
      setSearch(new_filter);
    },
    [canSetFilter, setFilter, setSearch],
  );
  const replaceFilter = useCallback(
    (new_filter: GroupedFilter) => {
      setFilter(new_filter);
      queueMacrotask(() => setSearch(new_filter, true));
    },
    [setFilter, setSearch],
  );
  const filterFunction = useMemo(
    () => getGroupedFilterFunction(filter),
    [filter],
  );
  const clearFilters = useCallback(() => {
    selectFilter(defaultGroupedFilter(FilterGroupType.Task));
  }, [selectFilter]);

  useEffect(() => {
    if (
      !isEqual(
        {
          type: search.type,
          defaultGroup: search.defaultGroup,
          groupCriteria: search.groupCriteria,
        },
        {
          type: filter.type,
          defaultGroup: filter.defaultGroup,
          groupCriteria: filter.groupCriteria.filter(({ criteria }) =>
            FieldFilter.isValid(criteria),
          ),
        },
      )
    ) {
      setFilter(search);
    }
  }, [search, filter, setFilter]);

  return {
    selectFilter,
    replaceFilter,
    clearFilters,
    filter,
    filterFunction,
  };
};
